import { FunctionComponent } from "react";

import { ListSourceType } from "@/enum/list.enum";

import { CustomEventType } from "@/enum/custom-event.enum";

import { StepInfo } from "@/components/StepInfo/StepInfo";
import { SelectWebsiteScrapeProduct } from "@/components/SelectWebsiteScrapeProduct/SelectWebsiteScrapeProduct";

import { CreateCustomEventForm } from "@/pages/Private/pages/CustomEvent/components/CreateCustomEventForm";
import { RescrapeConfirmModal } from "@/pages/Private/pages/CustomEvent/components/RescrapeConfirmModal";
import { CustomEvent } from "@/pages/Private/pages/CustomEvent/schema/customEvent";

import { DownloadSource } from "../../../components/DownloadSource/DownloadSource";
import { ScrapedFilesCard } from "../../../components/DownloadSource/ScrapedFilesCard";
import { List } from "../../../schema/list";
import { useRescrape } from "./useRescrape";

export interface CustomEventSourceSectionProps {
	data: List;
}

/**
 * "Scraping as a Service" source step (PREC-3091).
 *
 * Before the first file arrives this is just the request form. Once a file has been delivered the
 * form collapses to a summary — the delivered files and their field mapping are what the user works
 * with from then on — and the summary carries the entry point for ordering a re-scrape.
 */
export const CustomEventSourceSection: FunctionComponent<CustomEventSourceSectionProps> = ({
	data,
}) => {
	const customEvent = data.customEvents?.[0] as CustomEvent | undefined;

	const {
		scrapeRequests,
		canRequestRescrape,
		isRescrapePending,
		isCancelling,
		handleCancelRescrape,
		isModalOpen,
		isLoading,
		showBuyCredits,
		setShowBuyCredits,
		openModal,
		closeModal,
		handleConfirm,
		handleBuyCredits,
	} = useRescrape({ customEvent, list: data });

	if (data.sourceType !== ListSourceType.CUSTOM_EVENTS) {
		return null;
	}

	const hasDeliveredFile = !!data.csvEnrichments?.[0];

	return (
		<>
			{data.entityType === CustomEventType.ORGANIZATION && (
				<StepInfo
					description={
						<>
							<p>
								Just provide us your target site, credentials (if needed), delivery deadline and any
								scraping rules. After we are done with the scraping, you will be notified via email.
								Then you can map the columns you want—like company name, booth number or LinkedIn
								and set your contact-search criteria and filters. Once you activate the list, our
								system will run the scrape and build your contacts; you’ll just need to wait a short
								while for it to finish before downloading the results.
							</p>
						</>
					}
					title="Building a list of companies from a website or mobile app"
					videoId="eFQDDBHWZcA"
				/>
			)}
			{data.entityType === CustomEventType.PERSON && (
				<StepInfo
					description={
						<>
							<p>
								You just name your source, give the target website or app link, desired delivery
								date (with at least two weeks’ notice) and any scraping rules (plus login
								credentials and OTP if needed), then we send you a raw file to review or edit. After
								that you map your key fields, discard what you don’t need, set your enrichment and
								lead-qualification settings—and you’re done.
							</p>
						</>
					}
					title="Building a List of People from Websites or Mobile Apps"
					videoId="oqezlJmwp6M"
				/>
			)}

			{/* Two columns like the mockup and every other source type: the request on the left,
			    what came back from it on the right. Collapses to one column below the tablet
			    breakpoint. */}
			<div className="mt-8 flex flex-col gap-8 md:flex-row md:items-start">
				<div className="w-full min-w-0 md:w-1/2">
					<CreateCustomEventForm
						canRequestRescrape={canRequestRescrape && hasDeliveredFile}
						customEvent={customEvent}
						handleCancelRescrape={handleCancelRescrape}
						handleRequestRescrape={openModal}
						isCancelling={isCancelling}
						isRescrapePending={isRescrapePending}
						list={data}
					/>
				</div>

				{hasDeliveredFile && (
					<div className="flex w-full min-w-0 flex-col gap-6 md:w-1/2">
						<ScrapedFilesCard
							csvEnrichments={data.csvEnrichments || []}
							listId={data.id}
							scrapeRequests={scrapeRequests}
						/>
						<DownloadSource list={data} />
					</div>
				)}
			</div>

			<RescrapeConfirmModal
				handleBuyCredits={handleBuyCredits}
				handleClose={closeModal}
				handleConfirm={handleConfirm}
				isLoading={isLoading}
				isOpened={isModalOpen}
			/>
			<SelectWebsiteScrapeProduct setShow={setShowBuyCredits} show={showBuyCredits} />
		</>
	);
};
